import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";

/**
 * Listens for the global `chariot:permission-denied` event (dispatched by the
 * fetch interceptor whenever the backend refuses a request with
 * `requirePermission`) and shows a toast naming the specific permission.
 *
 * Deduplicates bursts (e.g., an autosave that fires 4 times) by tracking recently-seen
 * permission keys. Same key won't toast again within DEDUPE_MS.
 */
const DEDUPE_MS = 4000;
const TOAST_MS = 6000;

export default function PermissionDeniedToast() {
  const { permissionCatalog } = useAuth();
  const [toasts, setToasts] = useState([]); // [{ id, label, required, method }]
  const seenRef = useRef(new Map()); // key -> timestamp

  useEffect(() => {
    const handler = (event) => {
      const { required, method } = event.detail || {};
      const now = Date.now();
      const key = required || "(unknown)";

      const lastSeen = seenRef.current.get(key) || 0;
      if (now - lastSeen < DEDUPE_MS) return;
      seenRef.current.set(key, now);

      const entry = permissionCatalog?.find((p) => p.key === required);
      const label = entry?.label || required || "a required permission";

      const id = `${now}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((prev) => [...prev, { id, label, required, method }]);

      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, TOAST_MS);
    };

    window.addEventListener("chariot:permission-denied", handler);
    return () => window.removeEventListener("chariot:permission-denied", handler);
  }, [permissionCatalog]);

  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        maxWidth: 420,
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role="alert"
          style={{
            background: "#fff",
            border: "1px solid #fecaca",
            borderLeft: "4px solid #dc2626",
            borderRadius: 8,
            padding: "12px 14px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            fontSize: 14,
            lineHeight: 1.45,
            color: "#111827",
          }}
        >
          <div style={{ fontWeight: 600, color: "#991b1b", marginBottom: 4 }}>
            Permission required
          </div>
          <div>
            You don't have permission to <strong>{t.label}</strong>.
          </div>
          {t.required && (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
              Ask your manager to enable <code style={{
                background: "#f3f4f6",
                padding: "1px 5px",
                borderRadius: 3,
                fontSize: 11,
              }}>{t.required}</code> on your account.
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
